import React, { useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PostPin } from "types/Post";
import { AcknowledgementsDialog } from "./AcknowledgementsDialog";

interface AcknowledgementSectionProps {
  /** The pin of a post that asks everybody to acknowledge it. */
  pin: PostPin;
  companyId: string;
  postId: string;
  /** Whether the post is for the whole group (the list of people then shows their companies). */
  isGroupPost: boolean;
  /** The post's author and its company's organisers may see who has not acknowledged yet. */
  canSeePending: boolean;
  /** Reports a failed request itself. */
  onAcknowledge: () => Promise<void>;
}

/** "Susipažinau" button for the expected people (a done mark once pressed; there is no undo), how many have read the post, and who has not. */
export function AcknowledgementSection({
  pin,
  companyId,
  postId,
  isGroupPost,
  canSeePending,
  onAcknowledge,
}: AcknowledgementSectionProps) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);

  const acknowledge = async () => {
    setIsSaving(true);
    try {
      await onAcknowledge();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      {pin.acknowledgedByMe && (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-green-700">
          <Check className="h-4 w-4" />
          {t("news_ack_done")}
        </span>
      )}
      {!pin.acknowledgedByMe && pin.expectsMe && (
        <button
          type="button"
          onClick={acknowledge}
          disabled={isSaving}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {t("news_ack_button")}
        </button>
      )}
      <span className="text-sm text-gray-700">{t("news_ack_count", { done: pin.ackCount, total: pin.ackTotal })}</span>
      {canSeePending && (
        <button type="button" onClick={() => setIsListOpen(true)} className="text-sm text-blue-600 hover:underline">
          {t("news_ack_pending_link")}
        </button>
      )}
      {isListOpen && (
        <AcknowledgementsDialog
          companyId={companyId}
          postId={postId}
          showCompany={isGroupPost}
          onClose={() => setIsListOpen(false)}
        />
      )}
    </div>
  );
}
